import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { clientApi } from "../../api";
import { formatDateTimeAU } from "../../utils/formatDate";

/* ── Human labels + icons for each action ─────────────────────────────
   Kept in a single map so the table row, modal header, and any
   provider-aware copy stay in sync. Actions not in the map fall back
   to the raw action string. */
const ACTION_META = {
  join:              { label: "New member signup",   tone: "success" },
  renew:             { label: "Membership renewal",   tone: "success" },
  update:            { label: "Profile updated",      tone: "info"    },
  update_profile:    { label: "Profile updated",      tone: "info"    }, // IGT naming
  pin_reset:         { label: "PIN reset",            tone: "warn"    },
  pin_change:        { label: "PIN changed",          tone: "warn"    },
  pin_verify:        { label: "PIN verified",         tone: "info"    },
  points_award:      { label: "Points awarded",       tone: "success" },
  points_redeem:     { label: "Points redeemed",      tone: "info"    },
  card_replace:      { label: "Card replaced",        tone: "warn"    },
  card_issue:        { label: "Digital pass issued",  tone: "success" },
  card_reissue:      { label: "Digital pass re-issued", tone: "warn"  },
  card_activate:     { label: "Pass activated",       tone: "info"    },
  card_deactivate:   { label: "Pass deactivated",     tone: "warn"    },
  pass_issue:        { label: "Digital pass issued",  tone: "success" },
  pass_status_change:{ label: "Pass status changed",  tone: "info"    },
  merge:             { label: "Members merged",       tone: "warn"    },
  suspend:           { label: "Member suspended",     tone: "warn"    },
  unsuspend:         { label: "Member un-suspended",  tone: "info"    },
};

function actionLabel(a) { return ACTION_META[a]?.label || a?.replace(/_/g, " ") || "—"; }

function fmtDateTime(iso) {
  return formatDateTimeAU(iso);
}
function fmtCurrency(cents) {
  if (cents == null) return null;
  return `$${(Number(cents) / 100).toFixed(2)}`;
}
function fmtDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

/* Build a list of [label, value] rows rendering the action-specific
   metadata in a friendly way. Keeps the JSON blob digestible. */
function detailRowsFor(log) {
  const m = log.metadata || {};
  const a = log.action;
  const rows = [];

  if (a === "join" || a === "renew") {
    if (log.duration_years) rows.push(["Term", `${log.duration_years} year${log.duration_years > 1 ? "s" : ""}`]);
    if (log.amount_cents != null) rows.push(["Amount paid", fmtCurrency(log.amount_cents)]);
    if (log.stripe_pi_id) rows.push(["Stripe payment", <code key="pi" style={{ fontSize: "0.78rem" }}>{log.stripe_pi_id}</code>]);
  }
  if (a === "points_award" || a === "points_redeem") {
    if (m.points != null)   rows.push(["Points", String(m.points)]);
    if (m.pointType)        rows.push(["Point type", String(m.pointType)]);
    if (m.reason)           rows.push(["Reason", m.reason]);
    if (m.bulk)             rows.push(["Source", "Bulk award"]);
    if (m.terminalId)       rows.push(["Terminal", String(m.terminalId)]);
  }
  if (a === "update" || a === "update_profile") {
    const fields = Array.isArray(m.fields) ? m.fields : Object.keys(m.sections || {});
    if (fields.length) rows.push(["Changed fields", fields.join(", ")]);
    if (m.results) {
      const perSection = Object.entries(m.results).map(
        ([k, v]) => `${k}: ${v.ok ? "ok" : v.skipped ? "skipped" : "failed"}`
      );
      if (perSection.length) rows.push(["Sections", perSection.join(" · ")]);
    }
  }
  if (a === "pin_verify" || a === "pin_change" || a === "pin_reset") {
    if (m.via)     rows.push(["Identified by", m.via === "card" ? "Card scan" : "Member number"]);
    if (m.reason)  rows.push(["Reason", m.reason]);
  }
  if (a === "card_replace") {
    if (m.old_card)                 rows.push(["Old card", <code key="old" style={{ fontSize: "0.78rem" }}>{m.old_card}</code>]);
    if (m.new_card)                 rows.push(["New card", <code key="new" style={{ fontSize: "0.78rem" }}>{m.new_card}</code>]);
    if (m.reason)                   rows.push(["Reason", m.reason]);
    if (m.pin_reset !== undefined)  rows.push(["PIN reset", m.pin_reset ? "Yes" : "No"]);
    if (m.reissued_pass !== undefined) rows.push(["Digital pass reissued", m.reissued_pass ? "Yes" : "No"]);
  }
  if (a === "merge") {
    if (m.survivor) rows.push(["Survivor membership", <code key="sv" style={{ fontSize: "0.78rem" }}>{m.survivor}</code>]);
    if (m.reason)   rows.push(["Reason", m.reason]);
  }
  if (a === "suspend" || a === "unsuspend") {
    if (m.reason) rows.push(["Reason", m.reason]);
  }
  if (a === "pass_status_change" || a === "card_activate" || a === "card_deactivate") {
    if (m.new_status) rows.push(["New status", String(m.new_status)]);
  }
  if (a === "card_reissue") {
    if (m.superseded != null) rows.push(["Superseded passes", String(m.superseded)]);
  }
  return rows;
}

/* Svg icons — minimal set for the modal header/close. */
const Ico = {
  close: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  chev:  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
};

export default function ActivityLogs() {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [venue,     setVenue]     = useState(null);
  const [selected,  setSelected]  = useState(null);

  useEffect(() => {
    clientApi.getLogs({ limit: 200 }).then((d) => setLogs(d || [])).catch(() => {}).finally(() => setLoading(false));
    clientApi.getMe().then((me) => setVenue(me?.venue || null)).catch(() => {});
  }, []);

  // ESC closes the modal — nice touch.
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const providerType = venue?.provider_type || "s7000";
  const providerLabel = { igt: "IGT", swiftpos: "SwiftPOS", circlescan: "CircleScan", s7000: "S7000" }[providerType] || "S7000";

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Activity Logs</h1>
          <p className="page-subtitle">Every member action taken at this venue — click a row for full details</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : logs.length ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Action</th><th>Member</th><th>Status</th><th>Amount</th><th>By</th></tr>
            </thead>
            <tbody>
              {logs.map((l) => {
                const tone = ACTION_META[l.action]?.tone || "info";
                return (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(l); } }}
                    tabIndex={0}
                    role="button"
                    aria-label={`${actionLabel(l.action)} details`}
                    style={{ cursor: "pointer" }}
                    className="activity-row"
                  >
                    <td style={{ fontSize: "0.85rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {fmtDateTime(l.created_at)}
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      <span className={`log-action-chip tone-${tone}`}>{actionLabel(l.action)}</span>
                    </td>
                    <td>
                      {l.membership_id ? (
                        <span>
                          {(l.first_name || l.last_name) ? (
                            <>{l.first_name || ""} {l.last_name || ""} </>
                          ) : null}
                          <code style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>#{l.membership_id}</code>
                        </span>
                      ) : "—"}
                    </td>
                    <td><span className={`badge ${l.status}`}>{l.status}</span></td>
                    <td style={{ fontWeight: 600 }}>{fmtCurrency(l.amount_cents) || "—"}</td>
                    <td style={{ fontSize: "0.85rem" }}>{l.actor_name || l.actor_email || (l.action === "join" ? "Public signup" : "—")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No activity yet</h3>
          <p className="muted">Member actions — signups, renewals, profile changes — will appear here.</p>
        </div>
      )}

      {selected && (
        <LogDetailModal
          log={selected}
          providerType={providerType}
          providerLabel={providerLabel}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Minimal styling for the chip + hover row — lives alongside the
          component to avoid polluting App.css for a single page. */}
      <style>{`
        .activity-row:hover { background: var(--bg-subtle); }
        .log-action-chip {
          display: inline-block;
          padding: 0.15rem 0.55rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 500;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          color: var(--text);
        }
        .log-action-chip.tone-success { background: var(--success-soft, rgba(34,197,94,0.12)); border-color: rgba(34,197,94,0.35); color: var(--success, #16a34a); }
        .log-action-chip.tone-warn    { background: var(--warning-soft, rgba(245,158,11,0.12)); border-color: rgba(245,158,11,0.35); color: var(--warning, #d97706); }
        .log-action-chip.tone-info    { background: var(--accent-soft, rgba(59,130,246,0.12)); border-color: rgba(59,130,246,0.25); color: var(--accent, #2563eb); }

        .log-section { margin-bottom: 1.25rem; }
        .log-section:last-child { margin-bottom: 0; }
        .log-section-title {
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--text-muted);
          margin: 0 0 0.5rem;
        }
        .log-kv { display: grid; grid-template-columns: 140px 1fr; gap: 0.3rem 0.75rem; font-size: 0.88rem; }
        .log-kv dt { color: var(--text-muted); }
        .log-kv dd { margin: 0; color: var(--text); word-break: break-word; }

        .log-call-list { display: flex; flex-direction: column; gap: 0.35rem; }
        .log-call {
          display: grid;
          grid-template-columns: 44px 48px 1fr auto auto;
          gap: 0.55rem;
          align-items: center;
          padding: 0.45rem 0.6rem;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.82rem;
        }
        .log-call-method { font-family: monospace; font-weight: 600; font-size: 0.72rem; color: var(--text-muted); }
        .log-call-status { font-family: monospace; font-size: 0.78rem; padding: 0.05rem 0.35rem; border-radius: 4px; }
        .log-call-status.ok { background: var(--success-soft); color: var(--success); }
        .log-call-status.err { background: var(--danger-soft); color: var(--danger); }
        .log-call-path { font-family: monospace; font-size: 0.8rem; word-break: break-all; color: var(--text); }
        .log-call-time { font-size: 0.78rem; color: var(--text-muted); font-variant-numeric: tabular-nums; }
        .log-call-detail { grid-column: 1 / -1; padding-top: 0.2rem; font-size: 0.75rem; color: var(--text-muted); font-family: monospace; }

        .log-raw-toggle {
          background: none; border: 0; padding: 0; color: var(--text-muted);
          font-size: 0.78rem; cursor: pointer; display: inline-flex; align-items: center; gap: 0.3rem;
        }
        .log-raw-toggle:hover { color: var(--text); }
        .log-raw-pre {
          margin: 0.4rem 0 0; padding: 0.6rem 0.75rem; background: var(--bg-elevated);
          border: 1px solid var(--border); border-radius: 6px; font-size: 0.72rem;
          color: var(--text); overflow: auto; max-height: 260px;
        }
      `}</style>
    </ClientLayout>
  );
}

/* ══════════════════════════════════════════════
   LogDetailModal — everything we know about one log row
   ══════════════════════════════════════════════ */
function LogDetailModal({ log, providerType, providerLabel, onClose }) {
  const [showRaw, setShowRaw] = useState(false);
  const tone   = ACTION_META[log.action]?.tone || "info";
  const label  = actionLabel(log.action);
  const detailRows = useMemo(() => detailRowsFor(log), [log]);

  const m      = log.metadata || {};
  // Filter calls to the ones that match the venue's provider. Historical
  // rows may not have a calls array at all (pre-instrumentation), in which
  // case we show an empty state.
  const allCalls = Array.isArray(m.calls) ? m.calls : [];
  const calls = allCalls; // show all providers; label each so cross-provider steps remain visible

  const metaForRaw = useMemo(() => {
    // Hide the calls array from the raw view (it has its own section)
    if (!m || Object.keys(m).length === 0) return null;
    const { calls: _c, ...rest } = m;
    return Object.keys(rest).length ? rest : null;
  }, [m]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className={`modal-header-icon`} style={{ background: `var(--${tone === "success" ? "success" : tone === "warn" ? "warning" : "accent"}-soft, rgba(59,130,246,0.12))` }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>#</span>
          </div>
          <div className="modal-header-text">
            <h2>{label}</h2>
            <p>{fmtDateTime(log.created_at)} · <span className={`badge ${log.status}`}>{log.status}</span></p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            {Ico.close}
          </button>
        </div>

        <div className="modal-body">
          {/* Who / when */}
          <section className="log-section">
            <h3 className="log-section-title">Who &amp; where</h3>
            <dl className="log-kv">
              <dt>Actor</dt>
              <dd>{log.actor_name || log.actor_email || (log.action === "join" ? "Public signup" : "System")}</dd>
              {log.actor_email && log.actor_name && (<><dt>Email</dt><dd>{log.actor_email}</dd></>)}
              <dt>IP address</dt>
              <dd>{log.ip_address || "—"}</dd>
              <dt>Venue provider</dt>
              <dd>{providerLabel}</dd>
            </dl>
          </section>

          {/* Member */}
          <section className="log-section">
            <h3 className="log-section-title">Member</h3>
            <dl className="log-kv">
              <dt>Name</dt>
              <dd>
                {log.membership_id ? (
                  <Link to={`/client/members/${log.membership_id}`} onClick={onClose}>
                    {[log.first_name, log.last_name].filter(Boolean).join(" ") || "(not captured)"}
                  </Link>
                ) : "—"}
              </dd>
              <dt>Membership #</dt>
              <dd>{log.membership_id ? <code style={{ fontSize: "0.82rem" }}>{log.membership_id}</code> : "—"}</dd>
            </dl>
          </section>

          {/* Action-specific details */}
          {detailRows.length > 0 && (
            <section className="log-section">
              <h3 className="log-section-title">Details</h3>
              <dl className="log-kv">
                {detailRows.map(([k, v], i) => (
                  <React.Fragment key={i}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
          )}

          {/* Error */}
          {log.status !== "success" && log.error_message && (
            <section className="log-section">
              <h3 className="log-section-title" style={{ color: "var(--danger, #dc2626)" }}>Error</h3>
              <div style={{
                padding: "0.6rem 0.8rem",
                background: "var(--danger-soft, rgba(239,68,68,0.1))",
                border: "1px solid var(--danger, #dc2626)",
                borderRadius: 6,
                fontSize: "0.85rem",
                color: "var(--danger, #dc2626)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}>
                {log.error_message}
              </div>
            </section>
          )}

          {/* Provider API calls — only meaningful for rows captured after
              the apiTrace middleware was added; older rows show empty state. */}
          <section className="log-section">
            <h3 className="log-section-title">
              Provider API calls {calls.length > 0 ? `(${calls.length})` : ""}
            </h3>
            {calls.length === 0 ? (
              <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                No {providerLabel} calls recorded for this event.
              </div>
            ) : (
              <div className="log-call-list">
                {calls.map((c, i) => (
                  <div key={i} className="log-call">
                    <span className="log-call-method">
                      <span style={{
                        textTransform: "uppercase",
                        color: { igt: "var(--accent)", circlescan: "var(--success)", swiftpos: "var(--warning)", s7000: "var(--text-secondary)" }[c.provider] || "var(--text-secondary)",
                        fontSize: "0.65rem",
                        display: "block",
                      }}>{c.provider}</span>
                      {c.method}
                    </span>
                    <span className={`log-call-status ${c.ok ? "ok" : "err"}`}>
                      {c.status ?? (c.ok ? "ok" : "err")}
                    </span>
                    <span className="log-call-path">{c.path}</span>
                    <span className="log-call-time">{fmtDuration(c.duration_ms)}</span>
                    <span className="log-call-time">
                      {c.at ? new Date(c.at).toLocaleTimeString() : ""}
                    </span>
                    {(c.detail || c.error) && (
                      <span className="log-call-detail">
                        {c.error ? <span style={{ color: "var(--danger, #dc2626)" }}>{c.error}</span> : c.detail}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Raw JSON for power users */}
          {metaForRaw && (
            <section className="log-section">
              <button className="log-raw-toggle" onClick={() => setShowRaw((v) => !v)}>
                {Ico.chev} {showRaw ? "Hide" : "Show"} raw metadata
              </button>
              {showRaw && (
                <pre className="log-raw-pre">{JSON.stringify(metaForRaw, null, 2)}</pre>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
